import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { getSiteSettingsCached } from "../api/siteSettings";

const COLOR_OPTIONS = [
  { hex: "#FFD700", rgb: "255, 215, 0", dark: "#b8960a", label: "Gold" },
  { hex: "#FAF9F6", rgb: "250, 249, 246", dark: "#c8c7c5", label: "Off White" },
  { hex: "#0FFF50", rgb: "15, 255, 80", dark: "#0ab336", label: "Neon Green" },
  { hex: "#1F51FF", rgb: "31, 81, 255", dark: "#1438b3", label: "Neon Blue" },
  { hex: "#0068BA", rgb: "0, 104, 186", dark: "#004982", label: "Cobalt Blue" },
  { hex: "#40E0D0", rgb: "64, 224, 208", dark: "#2a9990", label: "Turquoise" },
  { hex: "#CF9FFF", rgb: "207, 159, 255", dark: "#9b6acc", label: "Light Violet" },
  { hex: "#FF10F0", rgb: "255, 16, 240", dark: "#b30ba8", label: "Neon Pink" },
  { hex: "#F33A6A", rgb: "243, 58, 106", dark: "#b01a47", label: "Rose" },
  { hex: "#DC143C", rgb: "220, 20, 60", dark: "#9a0e2a", label: "Crimson" },
  { hex: "#FF5F1F", rgb: "255, 95, 31", dark: "#b34215", label: "Neon Orange" },
  { hex: "#FAFA33", rgb: "250, 250, 51", dark: "#b3b324", label: "Lemon Yellow" },
  { hex: "#AAFF00", rgb: "170, 255, 0", dark: "#77b300", label: "Bright Green" },
  { hex: "#2F7085", rgb: "47, 112, 133", dark: "#214e5d", label: "Petrol" },
];

const DEFAULT = COLOR_OPTIONS[0];

const AccentContext = createContext();

function findColor(hex) {
  return COLOR_OPTIONS.find((c) => c.hex === hex) || DEFAULT;
}

function getCachedColor() {
  const saved = localStorage.getItem("accentColor");
  return saved ? findColor(saved) : DEFAULT;
}

export function AccentProvider({ children }) {
  const [active, setActive] = useState(getCachedColor);

  // Fetch server-side accent on mount
  useEffect(() => {
    getSiteSettingsCached()
      .then((data) => {
        if (data?.accentColor) {
          const color = findColor(data.accentColor);
          setActive(color);
          localStorage.setItem("accentColor", color.hex);
        }
      })
      .catch(() => {
        // Fall back to localStorage cache (already set as initial state)
      });
  }, []);

  const setAccent = useCallback((color) => {
    setActive(color);
    localStorage.setItem("accentColor", color.hex);
  }, []);

  return (
    <AccentContext.Provider value={{ active, setAccent, colors: COLOR_OPTIONS }}>
      {children}
    </AccentContext.Provider>
  );
}

export function useAccent() {
  return useContext(AccentContext);
}
